import { Terminal, XCircle } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  isRunResult,
  type TestFailure,
  useExecutionOptional,
} from './execution-context'
import { FailurePanel } from './test-results/failure-panel'
import { RawResultsList } from './test-results/raw-results-list'
import { RunResultsView } from './test-results/run-results-view'
import { SubmitResultView } from './test-results/submit-result-view'

export function TestResultsPane() {
  const executionContext = useExecutionOptional()

  const result = executionContext?.result ?? null
  const error = executionContext?.error ?? null
  const isExecuting = executionContext?.isExecuting ?? false
  const executionMode = executionContext?.executionMode ?? null

  if (isExecuting) {
    return <LoadingSkeleton />
  }

  if (!result && !error) {
    return <EmptyState />
  }

  // Handle error from the hook itself (e.g., network error, auth error)
  if (error) {
    return <ErrorDisplay message={error.message} />
  }

  // Handle structured results from run/submit execution
  if (result && isRunResult(result)) {
    // Structured program-level failure (user syntax error, import-time
    // crash) — takes precedence over the legacy string fallbacks below,
    // which the backend still derives from it during the transition.
    if (result.programFailure) {
      return <ProgramFailureDisplay failure={result.programFailure} />
    }

    // Whole-program errors (driver-crash fallback) render as a panel
    if (result.compilationError) {
      return (
        <ErrorDisplay
          message={result.compilationError}
          title="Compilation Error"
        />
      )
    }

    if (result.runtimeError) {
      return (
        <ErrorDisplay message={result.runtimeError} title="Runtime Error" />
      )
    }

    // Raw test scripts keep their legacy per-script list (spec §1).
    // Keyed by execution so each row's auto-expand state resets per run,
    // mirroring RunResultsView below.
    if (result.testType === 'raw') {
      return (
        <RawResultsList
          key={result.executionId}
          results={result.results}
          summary={result.summary}
        />
      )
    }

    // Submit: Accepted state or single first-failure card
    if (executionMode === 'submit') {
      return <SubmitResultView result={result} />
    }

    // Run: per-case tabs. Keyed by execution so tab selection resets per run.
    return (
      <RunResultsView
        key={result.executionId}
        results={result.results}
        summary={result.summary}
      />
    )
  }

  return <EmptyState />
}

// =============================================================================
// Shared pane states
// =============================================================================

function LoadingSkeleton() {
  return (
    <div className="h-full space-y-4 overflow-auto p-4">
      {/* Status skeleton */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-5 w-5 rounded-full" />
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-4 w-20" />
      </div>

      {/* Test case skeletons */}
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <div className="border-border/60 bg-muted/30 space-y-2 rounded-lg border p-3">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-5 w-32" />
          </div>
        </div>
      ))}

      {/* Running indicator */}
      <div className="text-muted-foreground flex items-center justify-center gap-2 pt-2">
        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
        <span className="text-sm">Running your code...</span>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="text-muted-foreground flex h-full flex-col items-center justify-center p-4 text-center">
      <Terminal className="mb-4 h-12 w-12 opacity-50" />
      <p>Run your code to see the results</p>
    </div>
  )
}

interface ErrorDisplayProps {
  message: string
  title?: string
}

function ErrorDisplay({ message, title = 'Error' }: ErrorDisplayProps) {
  return (
    <div className="h-full space-y-4 overflow-auto p-4">
      <div className="flex items-center gap-2">
        <XCircle className="h-5 w-5 text-red-500" />
        <h2 className="text-lg font-semibold text-red-500">{title}</h2>
      </div>

      <div className="rounded-lg bg-red-500/10 p-4">
        <pre className="border-0 font-mono text-sm whitespace-pre-wrap text-red-700 dark:text-red-300">
          {message}
        </pre>
      </div>
    </div>
  )
}

// Python compile-time error classes. TestFailure.kind has only two values
// (wrong_answer / runtime_error), so compile-vs-runtime classification for
// program-level failures is by errorType.
const COMPILE_ERROR_TYPES = new Set([
  'SyntaxError',
  'IndentationError',
  'TabError',
])

/**
 * Structured program-level failure (user syntax error, import-time crash).
 * FailurePanel renders the errorType + message headline and the
 * "around line N in fn" frame line, and publishes the Monaco gutter marker
 * via ErrorMarkerContext.
 */
function ProgramFailureDisplay({ failure }: { failure: TestFailure }) {
  const title =
    failure.errorType && COMPILE_ERROR_TYPES.has(failure.errorType)
      ? 'Compilation Error'
      : 'Runtime Error'

  return (
    <div className="h-full space-y-4 overflow-auto p-4">
      <div className="flex items-center gap-2">
        <XCircle className="h-5 w-5 text-red-500" />
        <h2 className="text-lg font-semibold text-red-500">{title}</h2>
      </div>
      <FailurePanel failure={failure} />
    </div>
  )
}
